import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { userPropTypes } from 'helpers/propTypes';

import './TopNavD8.scss';

const getFreeShippingThreshold = (envios) => {
  if (!Array.isArray(envios) || envios.length === 0) return null;
  const mins = envios
    .map((e) => Number(e?.monto_minimo || 0))
    .filter((n) => Number.isFinite(n) && n > 0);
  if (mins.length === 0) return null;
  return Math.min(...mins);
};

const TopNavD8 = (props) => {
  const {
    client, envios, minimumPurchase,
  } = props;

  const contactPhone = client?.phone ? String(client.phone) : '';
  const contactEmail = client?.email_info || client?.email || '';

  const messages = useMemo(() => {
    const items = [];
    const hasEnvios = Array.isArray(envios) && envios.length > 0;

    items.push('Envíos a todo el país');

    const minPurchase = Number(minimumPurchase || 0);
    if (Number.isFinite(minPurchase) && minPurchase > 0) {
      items.push(`Compra mínima: $${minPurchase.toLocaleString('es-AR')}`);
    } else {
      items.push('Comprá sin mínimo');
    }

    const freeFrom = getFreeShippingThreshold(envios);
    if (hasEnvios && freeFrom) {
      items.push(`Envío gratis desde $${Number(freeFrom).toLocaleString('es-AR')}`);
    } else if (hasEnvios) {
      items.push('Envíos sin monto mínimo');
    }

    return items.filter(Boolean);
  }, [envios, minimumPurchase]);

  const hasContactInfo = Boolean(contactPhone || contactEmail);
  const hasMessages = messages.length > 0;

  const [reduceMotion, setReduceMotion] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduceMotion(!!mq.matches);
    update();
    if (mq.addEventListener) mq.addEventListener('change', update);
    else if (mq.addListener) mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', update);
      else if (mq.removeListener) mq.removeListener(update);
    };
  }, []);

  const [index, setIndex] = useState(0);
  useEffect(() => {
    setIndex(0);
  }, [messages.length]);

  useEffect(() => {
    if (!hasMessages || messages.length <= 1) return undefined;

    const intervalMs = reduceMotion ? 4500 : 3000;
    const id = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % messages.length);
    }, intervalMs);

    return () => window.clearInterval(id);
  }, [hasMessages, messages, reduceMotion]);

  if (!hasContactInfo && !hasMessages) return null;

  return (
    <div className="top-nav-d8" data-testid="topnav">
      <div className="container">
        <div className="top-nav-content-d8">
          {hasContactInfo && (
            <div className="top-nav-info-d8">
              {contactPhone && (
                <a className="top-nav-item-d8 top-nav-border-right-d8" href={`tel:${contactPhone}`}>
                  {contactPhone}
                </a>
              )}
              {contactEmail && (
                <a className="top-nav-item-d8" href={`mailto:${contactEmail}`}>
                  {contactEmail}
                </a>
              )}
            </div>
          )}
          {hasMessages && (
            <div className="top-nav-messages-d8" aria-label="Avisos">
              <span
                key={`${index}-${messages[index]}`}
                className="top-nav-message-d8"
              >
                {messages[index]}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

TopNavD8.defaultProps = {
  user: null,
  client: null,
  envios: null,
  minimumPurchase: 0,
};

TopNavD8.propTypes = {
  logout: PropTypes.func,
  openLoginModal: PropTypes.func,
  openRegisterModal: PropTypes.func,
  user: userPropTypes,
  client: PropTypes.shape({
    phone: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    email: PropTypes.string,
    email_info: PropTypes.string,
  }),
  envios: PropTypes.arrayOf(PropTypes.shape({
    monto_minimo: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  })),
  minimumPurchase: PropTypes.number,
};

export default TopNavD8;
